import React, { useState, useRef } from 'react';
import {
	CCard, CCardBody, CCardHeader, CCol, CRow, CButton,
	CAccordion, CAccordionItem, CAccordionHeader, CAccordionBody,
	CFormInput, CFormSelect, CBadge,
} from '@coreui/react';
import { DataGrid } from '@mui/x-data-grid';
import { Link, useLoaderData, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import AxiosInstance from 'src/components/Axios';
import { cilSearch } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import { useSupportCategories } from 'src/hooks/useSupportCategories';
import { SESSION_STATUS_OPTIONS, SESSION_STATUS_COLORS, SESSION_STATUS_LABELS, formatDateTime } from 'src/constants/supportLetters';
import { isValidEmailFormat } from 'src/utils/email';
import { formatPhoneInput, isValidPhoneInput, PHONE_PREFIX } from 'src/utils/inputValidation';
import { toast, ToastContainer } from 'react-toastify';

const urlApi = '/support-sessions';
const urlSession = '/support-letters/session';
const itemsPerPage = 30;

export async function LoaderSupportLettersSessions() {
	return AxiosInstance.get(urlApi, { params: { page: 1, itemsPerPage } });
}

const SupportLettersSessions = () => {
	const loaderData = useLoaderData();
	const navigate = useNavigate();

	const allCategories = useSupportCategories();
	const categoryOptions = [{ value: -1, label: 'Без категорії' }, ...allCategories.map(c => ({ value: c.id, label: c.name }))];

	const [rows, setRows] = useState(loaderData?.data?.data ?? []);
	const [rowCountState, setRowCountState] = useState(loaderData?.data?.total ?? 0);
	const [showLoader, setShowLoader] = useState(false);
	const [page, setPage] = useState(0);

	const [statusFilter, setStatusFilter] = useState('');
	const [emailFilter, setEmailFilter] = useState('');
	const [phoneFilter, setPhoneFilter] = useState(PHONE_PREFIX);
	const [nameFilter, setNameFilter] = useState('');
	const [subjectFilter, setSubjectFilter] = useState('');
	const [categoryFilter, setCategoryFilter] = useState([]);
	const [filterErrors, setFilterErrors] = useState({});

	const currentFilters = useRef({});

	const getRowsTable = (page = 1, extraParams = {}) => {
		setShowLoader(true);
		const params = { page, itemsPerPage, ...extraParams };
		AxiosInstance.get(urlApi, { params })
			.then(res => {
				setRows(res.data.data ?? []);
				setRowCountState(res.data.total ?? 0);
			})
			.catch(() => { })
			.finally(() => setShowLoader(false));
	};

	const buildFilters = () => {
		const f = {};
		if (statusFilter) f.status = statusFilter;
		if (emailFilter.trim()) f.user_email = emailFilter.trim();
		if (phoneFilter !== PHONE_PREFIX) f.user_phone = phoneFilter;
		if (nameFilter.trim()) f.user_name = nameFilter.trim();
		if (subjectFilter.trim()) f.subject = subjectFilter.trim();
		if (categoryFilter.length) f.category_ids = categoryFilter.map(c => c.value);
		return f;
	};

	const handleSearch = () => {
		const newErrors = {};
		if (emailFilter.trim() && !isValidEmailFormat(emailFilter)) newErrors.email = true;
		if (phoneFilter !== PHONE_PREFIX && !isValidPhoneInput(phoneFilter)) newErrors.phone = true;

		if (Object.keys(newErrors).length) {
			setFilterErrors(newErrors);
			const msgs = [newErrors.email && 'Email', newErrors.phone && 'Телефон'].filter(Boolean);
			toast.warning(`Невірний формат: ${msgs.join(', ')}`);
			return;
		}

		setFilterErrors({});
		const f = buildFilters();
		currentFilters.current = f;
		setPage(0);
		getRowsTable(1, f);
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') handleSearch();
	};

	const handleClear = () => {
		setStatusFilter('');
		setEmailFilter('');
		setPhoneFilter(PHONE_PREFIX);
		setNameFilter('');
		setSubjectFilter('');
		setCategoryFilter([]);
		setFilterErrors({});
		currentFilters.current = {};
		setPage(0);
		getRowsTable(1, {});
	};

	const columns = [
		{ field: 'id', headerName: 'ID', width: 70 },
		{
			field: 'subject', headerName: 'Тема сесії', width: 260,
			renderCell: (params) => (
				<Link to={`${urlSession}/${params.row.id}`} className="table-name-link" title={params.row.subject || '—'}>
					{params.row.subject || '—'}
				</Link>
			),
		},
		{ field: 'user_email', headerName: 'Email', width: 250 },
		{ field: 'user_name', headerName: "Ім'я", width: 160 },
		{ field: 'user_phone', headerName: 'Телефон', width: 125 },
		{ field: 'requests_count', headerName: 'Листів', width: 80 },
		{
			field: 'status', headerName: 'Статус', width: 180,
			renderCell: (params) => (
				<CBadge color={SESSION_STATUS_COLORS[params.row.status] ?? 'secondary'}>
					{SESSION_STATUS_LABELS[params.row.status] ?? params.row.status}
				</CBadge>
			),
		},
		{
			field: 'action', headerName: 'Відкрити', headerAlign: 'center', align: 'center', sortable: false, width: 95,
			renderCell: (params) => (
				<Link
					to={`${urlSession}/${params.row.id}`}
					className="custom-view-icon"
					title="Відкрити сесію"
				>
					<CIcon icon={cilSearch} customClassName="nav-icon" height={18} />
				</Link >
			),
		},
		{
			field: 'categories', headerName: 'Категорії', width: 200,
			renderCell: (params) => {
				const text = (params.row.categories || []).map(c => c.name).join(', ') || '—';
				return <span title={text}>{text}</span>;
			},
		},
		{
			field: 'updated_at', headerName: 'Активність', width: 125,
			renderCell: (params) => formatDateTime(params.row.updated_at),
		},
		{
			field: 'updated_by', headerName: 'Оновив', width: 140,
			renderCell: (params) => params.row.updated_by?.username || '—',
		},
	];

	return (
		<>
			<ToastContainer />
			<CRow>
				<CCol xs={12}>
					<CCard className="mb-4">
						<CAccordion flush>
							<CAccordionItem>
								<CAccordionHeader><strong>Фільтр</strong></CAccordionHeader>
								<CAccordionBody>
									<CRow className="my-3 mx-2 g-2">
										<CCol md={2}>
											<CFormSelect
												value={statusFilter}
												onChange={e => setStatusFilter(e.target.value)}
												title="Фільтр по статусу сесії"
											>
												<option value="">Всі статуси</option>
												{SESSION_STATUS_OPTIONS.map(o => (
													<option key={o.value} value={o.value}>{o.label}</option>
												))}
											</CFormSelect>
										</CCol>
										<CCol md={2}>
											<CFormInput
												placeholder="Email"
												value={emailFilter}
												onChange={e => {
													setEmailFilter(e.target.value);
													if (filterErrors.email) setFilterErrors(p => ({ ...p, email: false }));
												}}
												onKeyDown={handleKeyDown}
												invalid={!!filterErrors.email}
												autoComplete="off"
												title="Пошук по email"
											/>
										</CCol>
										<CCol md={2}>
											<CFormInput
												placeholder="Телефон"
												value={phoneFilter}
												onChange={e => {
													const formatted = formatPhoneInput(e.target.value);
													if (formatted !== null) {
														setPhoneFilter(formatted);
														if (filterErrors.phone) setFilterErrors(p => ({ ...p, phone: false }));
													}
												}}
												onKeyDown={handleKeyDown}
												invalid={!!filterErrors.phone}
												autoComplete="off"
												title="Пошук по телефону"
											/>
										</CCol>
										<CCol md={2}>
											<CFormInput
												placeholder="Ім'я"
												value={nameFilter}
												onChange={e => setNameFilter(e.target.value)}
												onKeyDown={handleKeyDown}
												autoComplete="off"
												title="Пошук по імені"
											/>
										</CCol>
										<CCol md={2}>
											<CFormInput
												placeholder="Тема"
												value={subjectFilter}
												onChange={e => setSubjectFilter(e.target.value)}
												onKeyDown={handleKeyDown}
												autoComplete="off"
												title="Пошук по темі"
											/>
										</CCol>
										<CCol md={2} className="d-flex gap-2 align-items-center">
											<CButton color="primary" onClick={handleSearch} title="Застосувати фільтри">
												Пошук
											</CButton>
											<CButton color="secondary" variant="outline" onClick={handleClear} title="Скинути всі фільтри">
												Очистити
											</CButton>
										</CCol>
									</CRow>
									<CRow className="mx-2 g-2">
										<CCol md={4}>
											<Select
												isMulti
												options={categoryOptions}
												value={categoryFilter}
												onChange={setCategoryFilter}
												placeholder="Категорії..."
												isClearable
												classNamePrefix="react-select"
											/>
										</CCol>
									</CRow>
								</CAccordionBody>
							</CAccordionItem>
						</CAccordion>

						<CCardHeader><strong>Сесії листів</strong></CCardHeader>
						<CCardBody>
							<div style={{ height: 600, width: '100%' }}>
								<DataGrid
									rows={rows}
									columns={columns}
									page={page}
									pageSize={itemsPerPage}
									rowsPerPageOptions={[itemsPerPage]}
									pagination
									paginationMode="server"
									sortingMode="server"
									rowCount={rowCountState}
									loading={showLoader}
									onPageChange={(newPage) => {
										setPage(newPage);
										getRowsTable(newPage + 1, currentFilters.current);
									}}
								/>
							</div>
							<div className="d-flex justify-content-start mt-3">
								<CButton
									color="secondary"
									variant="outline"
									onClick={() => navigate(-1)}
									title="Повернутись до попередньої сторінки"
								>
									Повернутись
								</CButton>
							</div>
						</CCardBody>
					</CCard>
				</CCol>
			</CRow>
		</>
	);
};

export default SupportLettersSessions;
